/**
 * CountingQuiz Student Renderer
 * Student-style UI with 3D buttons, bright colors, and Lottie animations.
 * Uses useCountingQuiz headless hook for logic.
 */

import { cn } from '../../../lib/utils'
import { KatexText, SubmitButton, QuizSolveDescription, QuizMedia, QuizTypeBadge, QuizResultToggle, useQuizStyleVariant, type QuizResultToggleValue } from '../shared'
import type { QuizSolveResult } from '../../../types/legacyQuiz'
import type { CountingQuizData } from '../hooks'
import { formatText } from '../../../utils/quiz-parser'
import { useCountingQuiz } from '../hooks'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import Lottie from 'lottie-react'
import countingAnimation from '../counting/assets/quiz_counting.json'

interface CountingStudentProps {
  quizId: string | number
  quiz: CountingQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
  /** true면 결과 토글은 유지하되 풀이/해설 UI만 숨긴다. */
  hideSolveDescription?: boolean
  /** G4 — true면 숫자 입력 UI 외 텍스트와 문제 이미지를 흐린다. */
  dimChoiceText?: boolean
}

export function CountingStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound: _disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  dimChoiceText = false,
}: CountingStudentProps) {
  const { t } = useTranslation('quiz')
  const styleVariant = useQuizStyleVariant()

  const {
    displayValues,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    correctDigits: _correctDigits,
    showLottie,
    isDisabled,
    handleUp,
    handleDown,
    handleSubmit,
  } = useCountingQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  return (
    <div className="space-y-6">
      {/* Badge */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-2"
      >
        <QuizTypeBadge
          quizId={quizId}
          typeLabel={t('types.counting.label')}
          difficultyLevel={difficultyLevel}
          variant="student"
        />
      </motion.div>

      {/* Title + Problem + Media */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className={cn(
          styleVariant !== 'lc' && 'p-6 bg-linear-to-br to-gray-50 rounded-2xl from-slate-50',
          styleVariant !== 'lc' && 'border border-gray-100 shadow-2xs',
          'text-2xl font-medium leading-relaxed text-gray-900'
        )}
      >
        <span data-quiz-title-text className={cn(dimChoiceText && 'opacity-[0.05]')}>
          <KatexText text={quiz.title} />
        </span>
        {quiz.problem && (() => {
          const cleanProblem = quiz.problem.replace(/\{\{\}\}/g, '').trim()
          if (!cleanProblem) return null
          return (
            <div data-quiz-problem-text className={cn('mt-4 text-xl', dimChoiceText && 'opacity-[0.05]')}>
              {formatText(cleanProblem, `${quizId}-problem`, quiz.additionalImages ?? undefined)}
            </div>
          )
        })()}
        <QuizMedia
          image={quiz.image}
          imageMobile={quiz.imageMobile}
          desmos={quiz.desmos}
          vars={quiz.vars}
          className={cn(dimChoiceText && 'opacity-[0.05]')}
        />
      </motion.div>

      {/* Digit counters - student style with 3D buttons */}
      <div className="flex items-center justify-center gap-4 mt-6">
        {displayValues.map((value, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
            className={cn(
              'relative inline-flex flex-col items-center justify-center p-2 text-5xl font-semibold rounded-2xl',
              shouldShowCorrectAnswers || (isSolved && solveResult?.correct)
                ? 'bg-lc-mint-200 text-lc-mint-900'
                : isSolved && !solveResult?.correct
                ? 'bg-lc-red-100 text-lc-red-900'
                : 'bg-lc-lightblue-200 text-lc-gray-700'
            )}
          >
            {/* Up button - student 3D style */}
            <button
              onClick={() => handleUp(index)}
              disabled={isDisabled}
              className={cn(
                'w-20 h-10 rounded-xl flex items-center justify-center transition-all duration-150',
                !isDisabled
                  ? 'bg-lc-lightblue-900 [box-shadow:0px_3px_0px_#2E96CE] hover:bg-lc-lightblue-600 hover:[box-shadow:0px_3px_0px_#55C3FF] active:translate-y-px active:[box-shadow:unset]'
                  : 'bg-lc-gray-200 [box-shadow:0px_3px_0px_#AAAAAA]'
              )}
              aria-label={t('types.counting.incrementDigit', { position: index + 1 })}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M7.71515 15C6.96634 15 6.44661 14.6891 6.15596 14.0672C5.8642 13.4463 5.98179 12.8949 6.50873 12.413L10.8352 8.45652C11.0016 8.30435 11.1819 8.19022 11.376 8.11413C11.5701 8.03804 11.7781 8 12 8C12.2219 8 12.4299 8.03804 12.624 8.11413C12.8181 8.19022 12.9984 8.30435 13.1648 8.45652L17.4913 12.413C18.0182 12.8949 18.1358 13.4463 17.844 14.0672C17.5534 14.6891 17.0337 15 16.2849 15L7.71515 15Z"
                  fill={isDisabled ? '#AAAAAA' : 'white'}
                />
              </svg>
            </button>

            {/* Digit display or Lottie animation */}
            {showLottie ? (
              <div className="flex items-center justify-center" style={{ width: 80, height: 88 }}>
                <Lottie animationData={countingAnimation} loop autoplay style={{ width: 80, height: 88 }} />
              </div>
            ) : (
              <span data-quiz-counting-digit className="flex items-center justify-center h-[88px]">
                {value}
              </span>
            )}

            {/* Down button - student 3D style */}
            <button
              onClick={() => handleDown(index)}
              disabled={isDisabled}
              className={cn(
                'w-20 h-10 rounded-xl flex items-center justify-center transition-all duration-150',
                !isDisabled
                  ? 'bg-lc-lightblue-900 [box-shadow:0px_3px_0px_#2E96CE] hover:bg-lc-lightblue-600 hover:[box-shadow:0px_3px_0px_#55C3FF] active:translate-y-px active:[box-shadow:unset]'
                  : 'bg-lc-gray-200 [box-shadow:0px_3px_0px_#AAAAAA]'
              )}
              aria-label={t('types.counting.decrementDigit', { position: index + 1 })}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="rotate-180">
                <path
                  d="M7.71515 15C6.96634 15 6.44661 14.6891 6.15596 14.0672C5.8642 13.4463 5.98179 12.8949 6.50873 12.413L10.8352 8.45652C11.0016 8.30435 11.1819 8.19022 11.376 8.11413C11.5701 8.03804 11.7781 8 12 8C12.2219 8 12.4299 8.03804 12.624 8.11413C12.8181 8.19022 12.9984 8.30435 13.1648 8.45652L17.4913 12.413C18.0182 12.8949 18.1358 13.4463 17.844 14.0672C17.5534 14.6891 17.0337 15 16.2849 15L7.71515 15Z"
                  fill={isDisabled ? '#AAAAAA' : 'white'}
                />
              </svg>
            </button>
          </motion.div>
        ))}
      </div>

      {/* Submit button */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel}
        />
      )}

      {/* 풀이 결과 토글 (정답과 풀이 / 내가 쓴 답) */}
      {isSolved && !hideExplanation && (
        <QuizResultToggle
          value={isShowingCorrectAnswers ? 'correct-and-explanation' : 'my-answer'}
          onChange={(next: QuizResultToggleValue) => setIsShowingCorrectAnswers(next === 'correct-and-explanation')}
        />
      )}

      {/* 해설은 '정답과 풀이' 일 때만 노출 */}
      {isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && (
        <QuizSolveDescription
          description={solveResult?.description ?? undefined}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={false}
        />
      )}
    </div>
  )
}
